import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface UseAuthRedirectOptions {
  requiresHost?: boolean;
  onAuthRequired?: () => void;
  onUnauthorized?: () => void;
}

export const useAuthRedirect = (options: UseAuthRedirectOptions = {}) => {
  const { requiresHost = false, onAuthRequired, onUnauthorized } = options;
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isHost, isLoading } = useAuth();

  const checkAuthAndRedirect = useCallback((action?: () => void) => {
    // Don't check auth while loading
    if (isLoading) {
      return false;
    }

    // Check if user is authenticated
    if (!isAuthenticated) {
      // Build return URL with current path and search params
      const returnUrl = encodeURIComponent(location.pathname + location.search);
      
      // Call optional callback before redirect
      onAuthRequired?.();
      
      // Redirect to login with return URL
      navigate(`/auth/login?returnUrl=${returnUrl}`);
      return false;
    }

    // Check if host privileges are required
    if (requiresHost && !isHost()) {
      // Call optional callback for unauthorized access
      onUnauthorized?.();
      
      // Redirect to unauthorized page
      navigate('/unauthorized');
      return false;
    }

    // User is authenticated and authorized, execute action
    action?.();
    return true;
  }, [isAuthenticated, isHost, isLoading, requiresHost, location, navigate, onAuthRequired, onUnauthorized]);

  const requireAuth = useCallback((action?: () => void) => {
    return checkAuthAndRedirect(action);
  }, [checkAuthAndRedirect]);

  return {
    requireAuth,
    isAuthenticated,
    isHost: isHost(),
    isLoading,
  };
};

export default useAuthRedirect;